import React, { useEffect, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Cake, Gift, UserPlus, CalendarDays, Clock3, BellRing, ArrowRight } from "lucide-react";
import { getAuthJson } from "../utils/authStorage";

const EmployeeHome = memo(function EmployeeHome() {
  const [companyData, setCompanyData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load existing data first
    const storedData = getAuthJson("companyData");
    setCompanyData(storedData);
    console.log("[EmployeeHome] Loaded company data:", storedData);

    // 🆕 Listen for company data updates (from SuperAdmin)
    const handleCompanyUpdate = (e) => {
      console.log("[EmployeeHome] 🔄 Company updated via event:", e.detail);
      setCompanyData(e.detail);
    };

    window.addEventListener("companyDataUpdated", handleCompanyUpdate);
    return () =>
      window.removeEventListener("companyDataUpdated", handleCompanyUpdate);
  }, []);

  const birthdays = [
    { name: "Kranthi", team: "Engineering", when: "Today", note: "Backend Team", initials: "KR" },
    { name: "Nandhini", team: "People Ops", when: "Tomorrow", note: "Culture Committee", initials: "NA" },
    { name: "Rohit S", team: "Finance", when: "In 3 days", note: "Payroll Owner", initials: "RS" },
  ];
  const anniversaries = [
    { name: "Arun Prakash", years: 4, when: "Today", team: "Engineering" },
    { name: "Priya S", years: 2, when: "In 2 days", team: "Operations" },
    { name: "Anita M", years: 6, when: "This week", team: "People Ops" },
  ];
  const joinees = [
    { name: "Madhan Kumar", role: "Backend Developer", when: "Today", buddy: "Kranthi" },
    { name: "Shalini R", role: "HR Executive", when: "Next Monday", buddy: "Priya S" },
    { name: "Vignesh T", role: "QA Engineer", when: "Next Wednesday", buddy: "Arun P" },
  ];

  const quickActions = [
    { label: "Employee Directory", to: "/employee/profile", enabled: true },
    { label: "Attendance", to: "/employee/attendance", enabled: companyData?.module_attendance === 1 },
    { label: "Payroll", to: "/employee/payroll", enabled: companyData?.module_payroll === 1 },
    { label: "User Roles", to: "/employee/reports", enabled: true },
  ];

  return (
    <div className="p-6">
      <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden p-6 space-y-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {companyData?.logo ? (
                <img
                  src={companyData.logo}
                  alt={`${companyData.name} logo`}
                  className="h-12 w-12 rounded-md object-contain"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-redhalo-gray/40">
                  <BellRing className="h-6 w-6 text-redhalo-red" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Welcome back to PaceHRM</h1>
                <p className="text-gray-600">Stay updated with celebrations, onboarding, and day-to-day people actions.</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {quickActions.filter((a) => a.enabled).map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => navigate(action.to)}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-redhalo-red hover:text-redhalo-red"
                >
                  {action.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2">
              <Cake className="h-5 w-5 text-redhalo-red" />
              <h2 className="text-base font-semibold text-gray-900">Birthdays</h2>
            </div>
            <div className="space-y-3">
              {birthdays.map((item) => (
                <div key={item.name} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold bg-redhalo-gray/50 text-redhalo-red">
                        {item.initials}
                      </div>
                      <div>
                      <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-600">{item.team} - {item.when}</p>
                      <p className="text-[11px] mt-0.5 text-gray-500">{item.note}</p>
                      </div>
                    </div>
                    <button type="button" className="rounded-md bg-redhalo-red/10 px-2 py-1 text-[11px] font-semibold text-redhalo-red hover:bg-redhalo-red/20">Wish</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2">
              <Gift className="h-5 w-5 text-redhalo-red" />
              <h2 className="text-base font-semibold text-gray-900">Work Anniversaries</h2>
            </div>
            <div className="space-y-3">
              {anniversaries.map((item) => (
                <div key={item.name} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-600">{item.years} years - {item.when}</p>
                      <p className="text-[11px] mt-0.5 text-gray-500">{item.team}</p>
                    </div>
                    <button type="button" className="rounded-md bg-redhalo-red/10 px-2 py-1 text-[11px] font-semibold text-redhalo-red hover:bg-redhalo-red/20">Celebrate</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-redhalo-red" />
              <h2 className="text-base font-semibold text-gray-900">New Joinees</h2>
            </div>
            <div className="space-y-3">
              {joinees.map((item) => (
                <div key={item.name} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-600">{item.role} - {item.when}</p>
                      <p className="text-[11px] mt-0.5 text-gray-500">Buddy: {item.buddy}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button type="button" className="rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">Send Welcome</button>
                      <button type="button" className="rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:text-redhalo-red">View</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-redhalo-red" />
            <h2 className="text-base font-semibold text-gray-900">Today at a Glance</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {[
              { title: "Total Employees", value: "128", note: "Active workforce" },
              { title: "On Leave", value: "07", note: "Approved leaves today" },
              { title: "Pending Approvals", value: "05", note: "Attendance & requests" },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.title}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{item.value}</p>
                <p className="mt-1 text-xs text-gray-600">{item.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-redhalo-gray/40 px-3 py-2 text-xs text-gray-700">
            <Clock3 className="h-4 w-4 text-redhalo-red" />
            Tip: Use quick actions above to jump to attendance, payroll, and employee records.
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold transition bg-redhalo-red/10 text-redhalo-red hover:bg-redhalo-red/20">
              View Team Highlights
            </button>
            <button type="button" className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold transition bg-white text-gray-700 hover:text-redhalo-red border border-gray-200">
              Open Attendance Board
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default EmployeeHome;
